import logoImage from "../assets/images/logo.png";
import tabeebLogoImage from "../assets/images/tabeeb-logo.png";
import toothpickLogoImage from "../assets/images/toothpick-logo.png";
import paygexLogoImage from "../assets/images/paygex-logo.png";

/**
 * Retrieves the white label name for the application.
 * The value is fetched from the environment variable REACT_APP_WHITE_LABEL.
 * If the environment variable is not set, the default value 'PAYD' will be returned.
 *
 * @return {string} The white label name of the application.
 */
export function getWhiteLabelName() {
    return process.env.REACT_APP_WHITE_LABEL || 'PAYD';
}

/**
 * Compares the current white label name with the provided string.
 *
 * @param {string} [shouldBe='PAYD'] - The expected white label name to compare against.
 * @return {boolean} Returns true if the current white label name matches the provided string, otherwise false.
 */
export function whiteLabelNameIs(shouldBe = 'PAYD') {
    if (!shouldBe) return false;
    return getWhiteLabelName() === shouldBe;
}

/**
 * Determines if the current white label name is included in the provided list.
 *
 * @param {Array<string>} shouldBeOneOf - An array of white label names to check against.
 * @return {boolean} Returns true if the current white label name is found in the list, otherwise false.
 */
export function whiteLabelNameIn(shouldBeOneOf = []) {
    if (!shouldBeOneOf?.length) return false;
    return shouldBeOneOf.includes(getWhiteLabelName());
}

export function getLogoFileName() {
    const whiteLabelLogoMap = {
        'TOOTHPICK': toothpickLogoImage,
        'TABEEB': tabeebLogoImage,
        'PAYGEX': paygexLogoImage,
        'PAYD': logoImage,
    };

    const currentWhiteLabel = getWhiteLabelName();
    return whiteLabelLogoMap[currentWhiteLabel] || logoImage;
}

export function getAppName() {
    return process.env.REACT_APP_APP_NAME || 'PAYD';
}

export function getFaviconFileName() {
    const whiteLabelFaviconMap = {
        'TOOTHPICK': 'toothpick-favicon.ico',
        'TABEEB': 'tabeeb-favicon.ico',
        'PAYGEX': 'paygex-favicon.ico',
        'PAYD': 'favicon.ico'
    };

    const currentWhiteLabel = getWhiteLabelName();
    return whiteLabelFaviconMap[currentWhiteLabel] || 'favicon.ico';
}

export function setWhiteLabelMetadata() {
    document.title = getAppName();
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    const buildRef = Date.now();
    link.href = `/${getFaviconFileName()}?v=${buildRef}`;
    document.head.appendChild(link);
}

/**
 * Creates a chainable white label helper object with fluent API methods.
 * 
 * @return {object} An object with chainable methods for white label operations
 * 
 * @example
 * whiteLabel().is('PAYD')           // Returns true if current white label is 'PAYD'
 * whiteLabel().isNot('TABEEB')      // Returns true if current white label is not 'TABEEB'
 * whiteLabel().includes(['PAYD', 'TOOTHPICK']) // Returns true if current white label is in the array
 * whiteLabel().excludes(['TABEEB']) // Returns true if current white label is not in the array
 * whiteLabel().name()               // Returns the current white label name
 * whiteLabel().appName()            // Returns the application name
 * whiteLabel().logo()               // Returns the logo file path
 * whiteLabel().favicon()            // Returns the favicon filename
 * whiteLabel().initMetadata()       // Sets the white label metadata (title and favicon)
 */
export function whiteLabel() {
    return {
        /**
         * Checks if the current white label matches the specified value.
         * @param {string} value - The white label value to check against
         * @return {boolean} True if the current white label matches the value
         */
        is(value) {
            return whiteLabelNameIs(value);
        },

        /**
         * Checks if the current white label is NOT equal to the specified value.
         * @param {string} value - The white label value to check against
         * @return {boolean} True if the current white label does not match the value
         */
        isNot(value) {
            return !whiteLabelNameIs(value);
        },

        /**
         * Checks if the current white label is included in the provided array.
         * @param {Array<string>} values - An array of white label values to check against
         * @return {boolean} True if the current white label is found in the array
         */
        includes(values) {
            return whiteLabelNameIn(values);
        },

        /**
         * Checks if the current white label is NOT included in the provided array.
         * @param {Array<string>} values - An array of white label values to check against
         * @return {boolean} True if the current white label is not found in the array
         */
        excludes(values) {
            return !whiteLabelNameIn(values);
        },

        /**
         * Gets the current white label name.
         * @return {string} The current white label name
         */
        name() {
            return getWhiteLabelName();
        },

        /**
         * Gets the application name.
         * @return {string} The application name
         */
        appName() {
            return getAppName();
        },

        /**
         * Gets the logo file path for the current white label.
         * @return {string} The logo file path
         */
        logo() {
            return getLogoFileName();
        },

        /**
         * Gets the favicon filename for the current white label.
         * @return {string} The favicon filename
         */
        favicon() {
            return getFaviconFileName();
        },

        /**
         * Initializes the white label metadata (document title and favicon).
         * @return {void}
         */
        initMetadata() {
            return setWhiteLabelMetadata();
        }
    };
}